import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Home from './Home';

/**
 * HomeSectionWrapper
 * 
 * Renders the full homepage and auto-scrolls to the target section after hydration.
 * This allows direct URLs like /first-step, /testimonials etc. to be indexable
 * while maintaining the same UX as the scroll-based homepage navigation.
 */

// Correct section ID mapping (matches Home.js navLinks)
export const SECTION_ROUTES = {
  '/first-step': 'hero',
  '/baseline': 'intro',           // "Baseline" section has id="intro"
  '/your-stack': 'connected-world', // "Your Stack" section has id="connected-world"
  '/tools': 'tools',
  '/testimonials': 'testimonials',
  '/safety-check': 'cta',         // "Safety Check" section has id="cta"
  '/contact': 'contact'
};

// Route to nav label mapping (for URL-based routing)
export const ROUTE_TO_NAV = {
  '/first-step': 'First Step',
  '/baseline': 'Baseline',
  '/your-stack': 'Your Stack',
  '/tools': 'Tools',
  '/testimonials': 'Testimonials',
  '/safety-check': 'Safety Check',
  '/contact': 'Contact'
};

const HomeSectionWrapper = ({ targetSection }) => {
  const location = useLocation();
  const hasScrolled = useRef(false);
  
  useEffect(() => {
    // Reset scroll flag on route change
    hasScrolled.current = false;
    
    const scrollToSection = () => {
      if (hasScrolled.current) return;
      
      const element = document.getElementById(targetSection);
      if (element) {
        hasScrolled.current = true;
        
        // Get header height for offset (sticky header is ~80px)
        const headerOffset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        
        // Use instant scroll for initial load, smooth for subsequent
        window.scrollTo({
          top: offsetPosition,
          behavior: 'auto' // Use 'auto' for instant scroll on page load
        });
      }
    };
    
    // Wait for DOM to be fully painted and content to render
    // Use multiple rAF to ensure all content is rendered
    const timeoutId = setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollToSection();
        });
      });
    }, 50);
    
    return () => clearTimeout(timeoutId);
  }, [targetSection, location.pathname]);
  
  // Render the full homepage with initialSection prop
  return <Home initialSection={targetSection} />;
};

// Export individual section components for routing
export const FirstStepSection = () => <HomeSectionWrapper targetSection="hero" />;
export const BaselineSection = () => <HomeSectionWrapper targetSection="intro" />;
export const YourStackSection = () => <HomeSectionWrapper targetSection="connected-world" />;
export const ToolsSection = () => <HomeSectionWrapper targetSection="tools" />;
export const TestimonialsSection = () => <HomeSectionWrapper targetSection="testimonials" />;
export const SafetyCheckSection = () => <HomeSectionWrapper targetSection="cta" />;
export const SocialSection = () => <HomeSectionWrapper targetSection="social" />;
export const ContactSection = () => <HomeSectionWrapper targetSection="contact" />;

export default HomeSectionWrapper;
